import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Grid,
    Stack,
    Typography,
} from '@mui/material';
import { useFetch } from '../../hooks/useFetch';
import { useNavigate } from 'react-router-dom';
import useHandleErrors from '../../hooks/useHandleErrors';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CancelIcon from '@mui/icons-material/Cancel';
import PaidIcon from '@mui/icons-material/Paid';
import BlockIcon from '@mui/icons-material/Block';
import EventIcon from '@mui/icons-material/Event';

const StatCard = ({ value, label, icon, color, bgColor, onClick }) => (
    <Box
        onClick={onClick}
        sx={{
            bgcolor: 'white',
            borderRadius: '16px',
            border: '1px solid #e8e8e8',
            p: { xs: 2.5, sm: 3 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 140,
            cursor: onClick ? 'pointer' : 'default',
            transition: 'all 0.2s ease',
            '&:hover': onClick ? {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                borderColor: color,
            } : {},
        }}
    >
        <Box sx={{
            width: 44, height: 44,
            borderRadius: '12px',
            bgcolor: bgColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1.5,
        }}>
            {icon}
        </Box>
        <Typography variant="h4" fontWeight={700} color="#333" sx={{ lineHeight: 1 }}>
            {value}
        </Typography>
        <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ mt: 0.5, textAlign: 'center' }}>
            {label}
        </Typography>
    </Box>
);

export default () => {
    const navigate = useNavigate();
    const { data, error } = useFetch('/backoffice/dashboard');
    useHandleErrors(error);

    return (
        <Box sx={{
            maxWidth: 1100,
            mx: 'auto',
            p: { xs: '16px 12px 32px', sm: '24px 16px 48px' },
        }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight={600} color="#333">
                    Dashboard
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Visão geral da plataforma Empiria
                </Typography>
            </Box>

            {/* Alerta de pendentes */}
            {(data?.APPROVED_FALSE || 0) > 0 && (
                <Alert
                    severity="warning"
                    sx={{ mb: 3, width: '100%', borderRadius: '12px' }}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={() => navigate('/backoffice/usuarios')}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            Ver usuários
                        </Button>
                    }
                >
                    <AlertTitle>Pedidos de Anfitrião pendentes</AlertTitle>
                    {data.APPROVED_FALSE} anfitriao{data.APPROVED_FALSE > 1 ? 'es aguardam' : ' aguarda'} aprovacao.
                </Alert>
            )}

            {/* Usuarios */}
            <Typography variant="subtitle2" fontWeight={600} color="#555" sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}>
                Usuários
            </Typography>
            <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={6} sm={4} md={4}>
                    <StatCard
                        value={data?.USERS || 0}
                        label="Cadastros"
                        icon={<PeopleIcon sx={{ fontSize: 22, color: '#006a61' }} />}
                        color="#006a61"
                        bgColor="#e6f3f1"
                        onClick={() => navigate('/backoffice/usuarios')}
                    />
                </Grid>
                <Grid item xs={6} sm={4} md={4}>
                    <StatCard
                        value={data?.APPROVED_TRUE || 0}
                        label="Anfitriões aprovados"
                        icon={<CheckCircleIcon sx={{ fontSize: 22, color: '#2e7d32' }} />}
                        color="#2e7d32"
                        bgColor="#e8f5e9"
                        onClick={() => navigate('/backoffice/usuarios')}
                    />
                </Grid>
                <Grid item xs={6} sm={4} md={4}>
                    <StatCard
                        value={data?.APPROVED_FALSE || 0}
                        label="Anfitriões pendentes"
                        icon={<HourglassTopIcon sx={{ fontSize: 22, color: '#e65100' }} />}
                        color="#e65100"
                        bgColor="#fff3e0"
                        onClick={() => navigate('/backoffice/usuarios')}
                    />
                </Grid>
            </Grid>

            {/* Vendas */}
            <Typography variant="subtitle2" fontWeight={600} color="#555" sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}>
                Vendas
            </Typography>
            <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={6} sm={3} md={3}>
                    <StatCard
                        value={data?.SALE_CONCLUDED || 0}
                        label="Vendas pagas"
                        icon={<PaidIcon sx={{ fontSize: 22, color: '#2e7d32' }} />}
                        color="#2e7d32"
                        bgColor="#e8f5e9"
                        onClick={() => navigate('/backoffice/vendas')}
                    />
                </Grid>
                <Grid item xs={6} sm={3} md={3}>
                    <StatCard
                        value={data?.SALE_DRAFT || 0}
                        label="Sem pagamento"
                        icon={<ReceiptIcon sx={{ fontSize: 22, color: '#e65100' }} />}
                        color="#e65100"
                        bgColor="#fff3e0"
                        onClick={() => navigate('/backoffice/vendas')}
                    />
                </Grid>
                <Grid item xs={6} sm={3} md={3}>
                    <StatCard
                        value={data?.SALE_CANCELED || 0}
                        label="Canceladas"
                        icon={<CancelIcon sx={{ fontSize: 22, color: '#c62828' }} />}
                        color="#c62828"
                        bgColor="#fce4ec"
                        onClick={() => navigate('/backoffice/vendas')}
                    />
                </Grid>
                <Grid item xs={6} sm={3} md={3}>
                    <StatCard
                        value={data?.SALE_REJECTED || 0}
                        label="Rejeitadas"
                        icon={<BlockIcon sx={{ fontSize: 22, color: '#c62828' }} />}
                        color="#c62828"
                        bgColor="#fce4ec"
                        onClick={() => navigate('/backoffice/vendas')}
                    />
                </Grid>
            </Grid>

            {/* Encontros */}
            <Typography variant="subtitle2" fontWeight={600} color="#555" sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}>
                Encontros
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={6} sm={4} md={4}>
                    <StatCard
                        value={data?.MEETINGS || 0}
                        label="Encontros disponíveis"
                        icon={<EventIcon sx={{ fontSize: 22, color: '#006a61' }} />}
                        color="#006a61"
                        bgColor="#e6f3f1"
                    />
                </Grid>
            </Grid>
        </Box>
    );
};
